import { useState, useCallback, useEffect } from 'react';
import { DateTime } from 'luxon';
import { UserApiResponse } from '@/services/userService';
import { useNotifications } from '@/core/hooks/useNotifications';

export function useUserListPage(
    loadUsers: () => Promise<UserApiResponse[]>,
    loadUserById: (id: string) => Promise<UserApiResponse>,
    saveUser: (user: UserApiResponse) => Promise<void>,
    deleteUser: (id: string) => Promise<void>
) {
    const { notifySuccess, notifyErrorResponse } = useNotifications();

    const [loading, setLoading] = useState(false);
    const [loadingUser, setLoadingUser] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const [users, setUsers] = useState<UserApiResponse[]>([]);
    const [selected, setSelected] = useState<UserApiResponse | null>(null);
    const [itemToDelete, setItemToDelete] = useState<UserApiResponse | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined); // ← add

    const [sortBy, setSortBy] = useState('givenName');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setUsers(await loadUsers());
            setLastUpdate(DateTime.now());
        } catch (e) {
            notifyErrorResponse(e);
        } finally {
            setLoading(false);
        }
    }, [loadUsers, notifyErrorResponse]);

    useEffect(() => {
        load();
    }, []);

    const handleSave = useCallback(
        async (item: UserApiResponse) => {
            if (isSaving) return;
            setIsSaving(true);

            try {
                await saveUser(item);
                notifySuccess('successful');
                setSelected(null);
                load();
            } catch (e) {
                notifyErrorResponse(e);
            } finally {
                setIsSaving(false);
            }
        },
        [isSaving, saveUser, load, notifySuccess, notifyErrorResponse]
    );

    const handleDelete = useCallback(async () => {
        if (!itemToDelete?.id) return;

        setIsDeleting(true);
        try {
            await deleteUser(itemToDelete.id);
            notifySuccess('successful');
            setItemToDelete(null);
            setSelected(null);
            setDeleteDialogOpen(false);
            load();
        } catch (e) {
            notifyErrorResponse(e);
        } finally {
            setIsDeleting(false);
        }
    }, [itemToDelete, deleteUser, load]);

    const handleSelect = useCallback(
        async (item: UserApiResponse) => {
            if (!item.id) {
                setSelected(item);
                return;
            }

            setLoadingUser(true);
            try {
                setSelected(await loadUserById(item.id));
            } catch {
                setSelected(item);
            } finally {
                setLoadingUser(false);
            }
        },
        [loadUserById]
    );

    return {
        users,
        loading,
        loadingUser,
        isSaving,
        isDeleting,
        selected,
        setSelected,
        itemToDelete,
        setItemToDelete,
        deleteDialogOpen,
        setDeleteDialogOpen,
        sortBy,
        sortDirection,
        setSortBy,
        setSortDirection,
        load,
        handleSave,
        handleDelete,
        handleSelect,
        lastUpdate
    };
}
